import { toast } from "$global-providers/toast-provider";
import { Box, Button, styled, Typography } from "@material-ui/core";
import * as Sentry from "@sentry/react";
import { AccessionIdDisplay } from "../../../_lib/accession-id-display";
import { copyToClipboard } from "../../../_lib/copy-to-clipboard";
import { UltrasoundScanner } from "../../../_lib/epocus-icons";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";

const SectionCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const BodyText = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const Emphasis = styled("span")({
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const CopyButton = styled(Button)({
  borderRadius: 8,
  textTransform: "none",
  fontSize: rem(13),
  fontWeight: 600,
  borderColor: EPOCUS_PRIMARY,
  color: EPOCUS_PRIMARY,
  whiteSpace: "nowrap",
});

/**
 * Orders raised by a client that predates client-side minting can still arrive
 * without an accession. Rendering that as a blank field would tell the
 * performer to look for an order on the worklist that no probe can return, so
 * the two cases render differently.
 */
export const AwaitingImagesSection: FC<{
  accessionNumber: string | null;
  fromWorklist?: boolean;
}> = ({ accessionNumber, fromWorklist = false }) => {
  const handleCopy = async () => {
    if (!accessionNumber) return;
    try {
      await copyToClipboard(accessionNumber);
      toast.success("Accession ID copied to clipboard");
    } catch (clipboardError) {
      toast.error("Could not copy automatically — please copy the ID above");
      Sentry.captureException(clipboardError);
    }
  };

  return (
    <SectionCard>
      <Box display="flex" alignItems="center" style={{ gap: 10 }}>
        <UltrasoundScanner
          width={22}
          height={22}
          fill={EPOCUS_PRIMARY}
          aria-hidden
        />
        <Typography
          style={{
            fontWeight: 700,
            fontSize: rem(16),
            color: EPOCUS_TEXT_PRIMARY,
          }}
        >
          Awaiting images from device
        </Typography>
      </Box>

      {accessionNumber === null ? (
        <BodyText role="status">
          <Emphasis>No accession on this order.</Emphasis> Nothing links a scan
          back to it, so it will not appear on a probe's worklist. Raise the
          request again to get one.
        </BodyText>
      ) : (
        <>
          <BodyText>
            {fromWorklist ? (
              <>
                Select this patient on the probe's worklist, under accession{" "}
                <Emphasis>{accessionNumber}</Emphasis>. Images appear here
                automatically once the device sends them.
              </>
            ) : (
              <>
                Enter accession <Emphasis>{accessionNumber}</Emphasis> on the
                ultrasound machine to link the scan to this encounter. Images
                appear here automatically once the device sends them.
              </>
            )}
          </BodyText>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <AccessionIdDisplay aria-label={`Accession ID ${accessionNumber}`}>
              {accessionNumber}
            </AccessionIdDisplay>
            <CopyButton variant="outlined" size="small" onClick={handleCopy}>
              Copy ID
            </CopyButton>
          </Box>
        </>
      )}
    </SectionCard>
  );
};
